/**
 * Test Depresie. Patient Health Questionnaire-9 (PHQ-9).
 * Kroenke K, Spitzer RL, Williams JBW.
 * The PHQ-9: validity of a brief depression severity measure.
 * J Gen Intern Med. 2001;16(9):606-613. DOI 10.1046/j.1525-1497.2001.016009606.x
 *
 * Licence, verbatim from the footer of the official form (phqscreeners.com,
 * verified 2026-09-12/13, see monorepo/seo-report/briefs/instrumente-depresie-anxietate.md):
 *   "Developed by Drs. Robert L. Spitzer, Janet B.W. Williams, Kurt Kroenke and
 *   colleagues, with an educational grant from Pfizer Inc. No permission
 *   required to reproduce, translate, display or distribute."
 *
 * The Romanian wording below is an adaptation by Mindtale, NOT a validated
 * Romanian version. The only published Romanian validation found (Lupascu et
 * al., 2019) was run on patients with type 2 diabetes, not on the general
 * population.
 *
 * Item 9 asks about thoughts of death or self-harm. A non-zero answer there
 * surfaces the crisis block on the result screen whatever the total; that is
 * wired in DepresieRunner through TestRunner's `crisis` prop, not left to the
 * copy in this file. The last action of every category still carries the
 * crisis lines, because the result email does not know the item-level answers.
 */

import type { TestConfig } from "./types";

export type DepressionCategory =
  | "minimal"
  | "mild"
  | "moderate"
  | "moderately_severe"
  | "severe";

/** Zero-based index of the item about thoughts of death or self-harm. */
export const PHQ9_SELF_HARM_ITEM_INDEX = 8;

const QUESTIONS = [
  {
    id: 1,
    text: "Cât de des ai avut puțin interes sau puțină plăcere să faci lucruri?",
  },
  {
    id: 2,
    text: "Cât de des te-ai simțit abătut/ă, deprimat/ă sau fără speranță?",
  },
  {
    id: 3,
    text: "Cât de des ți-a fost greu să adormi sau să dormi fără să te trezești, ori ai dormit prea mult?",
  },
  {
    id: 4,
    text: "Cât de des te-ai simțit obosit/ă sau ai avut foarte puțină energie?",
  },
  {
    id: 5,
    text: "Cât de des ai avut poftă de mâncare scăzută sau ai mâncat peste măsură?",
  },
  {
    id: 6,
    text: "Cât de des te-ai simțit rău în legătură cu tine, ai simțit că ai eșuat sau că te-ai dezamăgit pe tine ori familia ta?",
  },
  {
    id: 7,
    text: "Cât de des ți-a fost greu să te concentrezi, de exemplu când citeai ziarul sau te uitai la televizor?",
  },
  {
    id: 8,
    text: "Cât de des te-ai mișcat sau ai vorbit atât de încet încât alții ar fi putut observa? Sau, dimpotrivă, ai fost atât de agitat/ă încât te-ai mișcat mult mai mult decât de obicei?",
  },
  {
    id: 9,
    text: "Cât de des ai avut gânduri că ar fi mai bine să nu mai trăiești sau gânduri de a-ți face rău într-un fel?",
  },
];

const OPTIONS = [
  { label: "Deloc", value: 0 },
  { label: "Câteva zile", value: 1 },
  { label: "Mai mult de jumătate din zile", value: 2 },
  { label: "Aproape în fiecare zi", value: 3 },
];

/* Kept identical in every category: the email only knows the category, so
   it cannot tell whether item 9 was answered, and has to say it to everyone. */
const CRISIS_ACTION =
  "Dacă la ultima întrebare ai răspuns altceva decât „Deloc”, vorbește azi cu cineva, nu la următoarea programare. Dacă ești în pericol acum, sună la 112, disponibil oricând. TelVerde Antisuicid, 0800 801 200, e gratuit, dar răspunde doar vineri-duminică, între 19:00 și 07:00.";

const UTM = "utm_source=email&utm_medium=transactional&utm_campaign=test_depresie";

export const depresieConfig: TestConfig<DepressionCategory> = {
  slug: "depresie",
  totalQuestions: QUESTIONS.length,
  estimatedMinutes: 3,
  /* The shared TestPageLayout does not render the hero; the page passes its
     own H1 and hook. These fields exist because the contract requires them. */
  hero: {
    eyebrow: "9 întrebări · 3 minute · gratuit",
    titlePart1: "Test de ",
    italicText: "depresie",
    titlePart2: " (PHQ-9)",
    subtitle:
      "Nouă întrebări despre ultimele două săptămâni. Rezultatul îți arată unde stai pe scala PHQ-9, nu ce diagnostic ai.",
    badges: [
      { text: "Patient Health Questionnaire-9 (PHQ-9)" },
      { text: "Adaptare în română de Mindtale" },
      { text: "Fără cont" },
    ],
    imageSrc: "",
    imageAlt: "",
  },
  questions: QUESTIONS,
  options: OPTIONS,
  scoring: {
    type: "sum",
    scoreMax: 27,
    scoreSuffix: "/27",
    allowedAnswerValues: [0, 1, 2, 3],
    categorize: (answers) => {
      if (answers.length !== QUESTIONS.length) {
        throw new Error("Toate întrebările trebuie completate");
      }
      const score = answers.reduce((acc, val) => acc + val, 0);
      let category: DepressionCategory;
      if (score <= 4) category = "minimal";
      else if (score <= 9) category = "mild";
      else if (score <= 14) category = "moderate";
      else if (score <= 19) category = "moderately_severe";
      else category = "severe";
      return { score, category };
    },
  },
  bands: [
    {
      category: "minimal",
      range: "0–4",
      label: "Simptome minime",
      caption: "Puține semne de depresie în ultimele două săptămâni.",
      bg: "#7DA86F",
    },
    {
      category: "mild",
      range: "5–9",
      label: "Depresie ușoară",
      caption: "Câteva simptome, prezente o parte din zile.",
      bg: "#E0B96C",
    },
    {
      category: "moderate",
      range: "10–14",
      label: "Depresie moderată",
      caption: "De la acest prag merită o evaluare făcută de un specialist.",
      bg: "#DC7050",
    },
    {
      category: "moderately_severe",
      range: "15–19",
      label: "Depresie moderat-severă",
      caption: "Simptome frecvente, care cer o evaluare în perioada următoare.",
      bg: "#B85037",
    },
    {
      category: "severe",
      range: "20–27",
      label: "Depresie severă",
      caption: "Simptome aproape zilnice. O evaluare cât mai curând.",
      bg: "#8A3A28",
    },
  ],
  resultCopy: {
    minimal: {
      headline: "Răspunsurile tale arată puține semne de depresie.",
      subheadline: "Un scor mic nu anulează o perioadă grea.",
      description:
        "În ultimele două săptămâni, simptomele despre care întreabă PHQ-9 au apărut rar sau deloc. Poți avea în continuare zile apăsătoare, iar dacă simți că ceva nu e în regulă, merită luat în serios chiar și cu un scor mic.",
      meaning:
        "Un scor între 0 și 4 intră în zona simptomelor minime pe scala PHQ-9. Chestionarul numără cât de des au apărut nouă simptome, deci nu surprinde tot ce trăiești și nu pune un diagnostic.",
      actions: [
        "Reia testul peste o lună sau atunci când simți că ceva s-a schimbat. Întrebările privesc doar ultimele două săptămâni.",
        "Dacă te apasă altceva decât ce a întrebat testul, de exemplu stresul, o pierdere sau o relație, o discuție cu un psihoterapeut e potrivită și fără un scor mare.",
        CRISIS_ACTION,
      ],
      ctaLabel: "Citește ghidul despre depresie",
      ctaTone: "soft",
      showTherapistMatch: false,
    },
    mild: {
      headline: "Câteva simptome de depresie au fost prezente.",
      subheadline: "Merită urmărite, fără alarmă.",
      description:
        "În ultimele două săptămâni ai avut o parte dintre simptomele despre care întreabă PHQ-9, în câteva dintre zile. Asta poate însoți o perioadă solicitantă și poate trece, dar poate fi și începutul a ceva ce merită discutat.",
      meaning:
        "Un scor între 5 și 9 intră în zona depresiei ușoare pe scala PHQ-9. Nu e un diagnostic. Contează mai ales dacă simptomele se mențin sau cresc în săptămânile următoare.",
      actions: [
        "Reia testul peste două-trei săptămâni. Dacă scorul crește sau rămâne la fel, e un motiv bun să vorbești cu un specialist.",
        "Păstrează câteva lucruri mici care îți fac bine, chiar dacă nu ai chef de ele: o plimbare, o oră fixă de trezire, un om cu care vorbești.",
        CRISIS_ACTION,
      ],
      ctaLabel: "Citește ghidul despre depresie",
      ctaTone: "soft",
      showTherapistMatch: false,
    },
    moderate: {
      headline: "Scorul tău trece de pragul la care merită o evaluare.",
      subheadline: "E un semnal, nu o etichetă.",
      description:
        "Simptomele despre care întreabă PHQ-9 au apărut des în ultimele două săptămâni. De la un scor de 10 în sus, autorii chestionarului consideră că merită o evaluare făcută de un specialist.",
      meaning:
        "Un scor între 10 și 14 intră în zona depresiei moderate pe scala PHQ-9. Ce înseamnă pentru tine, dacă e vorba de o depresie și ce ar ajuta, se stabilește într-o discuție cu un psihoterapeut, un psihiatru sau medicul de familie.",
      actions: [
        "Programează o discuție cu un psihoterapeut sau cu medicul de familie în perioada următoare și spune-i scorul. E un punct de plecare bun.",
        "Spune cuiva apropiat cum te simți. Nu trebuie să ai o explicație pentru ce trăiești ca să ceri sprijin.",
        CRISIS_ACTION,
      ],
      ctaLabel: "Vezi psihoterapeuți recomandați",
      ctaTone: "warm",
      showTherapistMatch: true,
    },
    moderately_severe: {
      headline: "Simptomele de depresie au fost frecvente.",
      subheadline: "Merită să nu duci asta singur/ă.",
      description:
        "În ultimele două săptămâni, multe dintre simptomele despre care întreabă PHQ-9 au apărut în mai mult de jumătate din zile. Asta cere, de obicei, energie pe care nu o mai ai, iar o evaluare în perioada următoare te poate ajuta să vezi ce se întâmplă.",
      meaning:
        "Un scor între 15 și 19 intră în zona depresiei moderat-severe pe scala PHQ-9. Scorul nu pune un diagnostic. Evaluarea și deciziile despre ce urmează le iei împreună cu un psihiatru, un psihoterapeut sau medicul de familie.",
      actions: [
        "Caută o evaluare în zilele următoare, nu peste câteva luni. Poți începe cu medicul de familie, un psihiatru sau un psihoterapeut.",
        "Spune cuiva apropiat ce ți-a ieșit și roagă-l să te ajute cu primul pas, de exemplu cu programarea.",
        CRISIS_ACTION,
      ],
      ctaLabel: "Găsește psihoterapeutul potrivit",
      ctaTone: "urgent",
      showTherapistMatch: true,
    },
    severe: {
      headline: "Scorul tău arată simptome de depresie aproape zilnice.",
      subheadline: "Merită ajutor cât mai curând.",
      description:
        "Aproape toate simptomele despre care întreabă PHQ-9 au fost prezente în cele mai multe zile din ultimele două săptămâni. Nu e o slăbiciune și nu e ceva ce trebuie să rezolvi singur/ă.",
      meaning:
        "Un scor între 20 și 27 intră în zona depresiei severe pe scala PHQ-9. Chestionarul nu pune un diagnostic, dar un scor atât de mare e un motiv să cauți o evaluare cât mai repede, la un psihiatru, la medicul de familie sau la un psihoterapeut.",
      actions: [
        "Caută o evaluare cât mai curând, ideal săptămâna aceasta. Medicul de familie te poate îndruma dacă nu știi de unde să începi.",
        "Nu rămâne singur/ă cu asta. Spune cuiva apropiat ce ți-a ieșit și ce simți.",
        CRISIS_ACTION,
      ],
      ctaLabel: "Găsește psihoterapeutul potrivit",
      ctaTone: "urgent",
      showTherapistMatch: true,
    },
  },
  source: {
    name: "Patient Health Questionnaire-9 (PHQ-9)",
    citationTitle: "The PHQ-9: validity of a brief depression severity measure.",
    authors: "Kroenke K, Spitzer RL, Williams JBW",
    journal: "Journal of General Internal Medicine",
    year: 2001,
    volume: "16(9):606-613",
    doi: "10.1046/j.1525-1497.2001.016009606.x",
    doiUrl: "https://doi.org/10.1046/j.1525-1497.2001.016009606.x",
    license:
      "Developed by Drs. Robert L. Spitzer, Janet B.W. Williams, Kurt Kroenke and colleagues, with an educational grant from Pfizer Inc. No permission required to reproduce, translate, display or distribute.",
    subsetNote:
      "Întrebările în română sunt o adaptare făcută de Mindtale, nu o versiune validată. Singura validare românească publicată găsită (Lupascu și colab., 2019) a fost făcută pe pacienți cu diabet de tip 2, nu pe populația generală.",
  },
  /* From the abstract of Kroenke et al. 2001. Not rendered by the layout; the
     page states only the figures it cites. */
  validity: {
    cronbachAlpha: 0.89,
    testRetestR: 0.84,
    sampleSize: 6000,
  },
  ui: {
    primaryAccent: "#2C969B",
    badgeDot: "#2C969B",
  },
  email: {
    subjectTemplate: (score, scoreMax, label) =>
      `Rezultatul tău: ${score}/${scoreMax} (${label})`,
    categoryStyles: {
      minimal: {
        label: "Simptome minime",
        range: "0–4",
        accent: "#5D8451",
        barBg: "#E8F0EA",
        barFg: "#264653",
        ctaLabel: "Citește ghidul despre depresie",
        ctaUrl: `https://mindtale.ro/depresie?${UTM}`,
      },
      mild: {
        label: "Depresie ușoară",
        range: "5–9",
        accent: "#A87D5C",
        barBg: "#F4E8D9",
        barFg: "#264653",
        ctaLabel: "Citește ghidul despre depresie",
        ctaUrl: `https://mindtale.ro/depresie?${UTM}`,
      },
      moderate: {
        label: "Depresie moderată",
        range: "10–14",
        accent: "#B85037",
        barBg: "#FBEAD5",
        barFg: "#264653",
        ctaLabel: "Vezi psihoterapeuți recomandați",
        ctaUrl: `https://mindtale.ro/gaseste-terapeut?energy=2&${UTM}`,
      },
      moderately_severe: {
        label: "Depresie moderat-severă",
        range: "15–19",
        accent: "#B85037",
        barBg: "#F6DCCB",
        barFg: "#264653",
        ctaLabel: "Găsește psihoterapeutul potrivit",
        ctaUrl: `https://mindtale.ro/gaseste-terapeut?energy=1&${UTM}`,
      },
      severe: {
        label: "Depresie severă",
        range: "20–27",
        accent: "#B85037",
        barBg: "#2D3F4A",
        barFg: "#FFF9F5",
        ctaLabel: "Găsește psihoterapeutul potrivit",
        ctaUrl: `https://mindtale.ro/gaseste-terapeut?energy=1&${UTM}`,
      },
    },
  },
  emailStarter: {
    title: "Ce poți încerca, indiferent de scor",
    intro:
      "Depresia scade tocmai energia de care ai nevoie ca să faci ceva pentru ea. De aceea pașii de mai jos sunt mici. Alege unul singur și încearcă-l azi.",
    exercises: [
      {
        name: "Un singur lucru mic, planificat",
        how: "Alege o activitate de 10-15 minute care îți făcea bine înainte, de exemplu o plimbare sau un telefon către cineva, și pune-o în calendar la o oră anume. O faci chiar dacă nu ai chef. Cheful vine adesea după, nu înainte.",
      },
      {
        name: "Aceeași oră de trezire",
        how: "Trezește-te la aceeași oră în fiecare zi, și în weekend, și ieși la lumina zilei în prima oră după trezire. Un ritm stabil de somn e unul dintre puținele lucruri pe care le poți regla când restul pare scăpat de sub control.",
      },
      {
        name: "Scrie ce ai făcut, nu ce n-ai făcut",
        how: "Seara, notează trei lucruri pe care le-ai făcut, oricât de mici: ai mâncat, ai răspuns la un mesaj, ai ieșit din casă. Depresia îți arată doar ce lipsește; lista îți arată și restul.",
      },
    ],
    suggestions: [
      "Spune unei persoane apropiate cum te simți.",
      "Dacă simptomele durează de mai multe săptămâni, vorbește cu medicul de familie sau cu un psihoterapeut.",
      "Dacă te gândești să îți faci rău, sună la 112, disponibil oricând.",
    ],
    book: "„The Mindful Way Through Depression”, Mark Williams, John Teasdale, Zindel Segal și Jon Kabat-Zinn",
  },
  therapistCTA: {
    utmMedium: "test_depresie",
    triggerCategories: ["moderate", "moderately_severe", "severe"],
    blocks: {
      moderate: {
        headline: "Vorbește cu cineva care lucrează cu depresia.",
        subline:
          "Câțiva psihoterapeuți care lucrează cu depresia. Prima discuție îți arată dacă vă potriviți, iar tu decizi dacă continui.",
        ctaLabel: "Vezi psihoterapeuții recomandați",
        energyParam: 2,
      },
      moderately_severe: {
        headline: "Nu trebuie să treci singur/ă prin asta.",
        subline:
          "Câțiva psihoterapeuți care lucrează cu depresia. Prima discuție îți arată dacă vă potriviți, iar tu decizi dacă continui.",
        ctaLabel: "Găsește psihoterapeutul potrivit",
        energyParam: 1,
      },
      severe: {
        headline: "Nu trebuie să treci singur/ă prin asta.",
        subline:
          "Câțiva psihoterapeuți care lucrează cu depresia. Dacă ești în pericol acum, nu aștepta o programare: sună la 112.",
        ctaLabel: "Găsește psihoterapeutul potrivit",
        energyParam: 1,
      },
    },
  },
  conditionCode: {
    code: "6A70",
    name: "Single episode depressive disorder",
    system: "ICD-11",
  },
  meta: {
    title: "Test depresie (PHQ-9): 9 întrebări, rezultat imediat | Mindtale",
    description:
      "Testul de depresie PHQ-9, în adaptarea Mindtale în română. 9 întrebări despre ultimele două săptămâni, gratuit și fără cont. Arată un nivel pe scală, nu un diagnostic.",
    canonicalUrl: "https://mindtale.ro/teste/depresie",
    keywords: [
      "test depresie",
      "test depresie online",
      "test depresie gratuit",
      "phq-9",
      "phq 9 romana",
      "chestionar depresie",
      "scala depresie",
      "am depresie test",
    ],
  },
  stepperFootnote:
    "Răspunde la cum te-ai simțit în ultimele 2 săptămâni. Nu există răspunsuri „corecte” sau „greșite”.",
  /* The shared runner has no intro card; kept for the contract. */
  intro: {
    eyebrow: "9 întrebări · 3 minute · gratuit",
    title: "Răspunde la cum te-ai simțit\nîn ultimele 2 săptămâni.",
    subtitle:
      "Contează tendința din ultimele două săptămâni, nu ziua de azi.",
    ctaLabel: "Începe testul",
  },
  bandsHeader: "Cele cinci zone (Kroenke et al., 2001)",
};
